import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, Clock, UserCheck, Stethoscope, CheckCircle, XCircle, 
  AlertTriangle, Phone, Plus, Search, RefreshCw, Play
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status colors for visual coding
const STATUS_CONFIG = {
  waiting: { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock, label: 'Waiting' },
  with_nurse: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: UserCheck, label: 'With Nurse' },
  with_doctor: { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Stethoscope, label: 'With Doctor' },
  completed: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle, label: 'Completed' },
  cancelled: { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: XCircle, label: 'Cancelled' },
  no_show: { color: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle, label: 'No Show' }
};

// Priority colors
const PRIORITY_CONFIG = {
  0: { color: 'bg-gray-50', label: 'Normal', badge: '' },
  1: { color: 'bg-amber-50', label: 'Urgent', badge: 'bg-amber-500 text-white' },
  2: { color: 'bg-red-50', label: 'Emergency', badge: 'bg-red-500 text-white' }
};

const PatientQueue = () => {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  
  // Add to queue form
  const [selectedPatient, setSelectedPatient] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [queueType, setQueueType] = useState('walk_in');
  const [priority, setPriority] = useState('0');
  const [notes, setNotes] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [queueRes, doctorsRes, patientsRes, statsRes] = await Promise.all([
        axios.get(`${API}/queue?include_completed=${showCompleted}`),
        axios.get(`${API}/doctors`),
        axios.get(`${API}/patients`),
        axios.get(`${API}/queue/stats`)
      ]);
      setQueue(queueRes.data);
      setDoctors(doctorsRes.data);
      setPatients(patientsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch queue:', error);
      toast.error('Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [showCompleted]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleAddToQueue = async (e) => {
    e.preventDefault();
    if (!selectedPatient) {
      toast.error('Please select a patient');
      return;
    }
    
    setAdding(true);
    try {
      await axios.post(`${API}/queue`, {
        patient_id: selectedPatient,
        queue_type: queueType,
        assigned_doctor_id: selectedDoctor || null,
        priority: parseInt(priority),
        notes: notes
      });
      toast.success('Patient added to queue');
      setShowAddModal(false);
      setSelectedPatient('');
      setSelectedDoctor('');
      setQueueType('walk_in');
      setPriority('0');
      setNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to queue');
    } finally {
      setAdding(false);
    }
  };

  const handleUpdateStatus = async (queueId, newStatus) => {
    try {
      await axios.put(`${API}/queue/${queueId}`, { status: newStatus });
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleAssignDoctor = async (queueId, doctorId) => {
    try {
      await axios.put(`${API}/queue/${queueId}`, { assigned_doctor_id: doctorId });
      toast.success('Doctor assigned');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign doctor');
    }
  };

  const handleStartEncounter = async (queueId) => {
    try {
      const response = await axios.post(`${API}/queue/${queueId}/start-encounter`);
      toast.success('Encounter started');
      // Navigate to encounter or refresh
      fetchData();
      window.location.href = `/encounters/${response.data.id}`;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start encounter');
    }
  };

  const handleRemoveFromQueue = async (queueId) => {
    if (!window.confirm('Remove this patient from queue?')) return;
    try {
      await axios.delete(`${API}/queue/${queueId}`);
      toast.success('Removed from queue');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    }
  };

  // Filter queue by search
  const filteredQueue = queue.filter(item => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      item.patient_name?.toLowerCase().includes(search) ||
      item.first_name?.toLowerCase().includes(search) ||
      item.last_name?.toLowerCase().includes(search) ||
      item.phone?.includes(search)
    );
  });

  // Separate walk-ins and appointments
  const walkIns = filteredQueue.filter(q => q.queue_type === 'walk_in');
  const appointments = filteredQueue.filter(q => q.queue_type === 'appointment');

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getWaitTime = (checkInTime) => {
    if (!checkInTime) return '-';
    const checkIn = new Date(checkInTime);
    const now = new Date();
    const diffMs = now - checkIn;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  const canModifyQueue = ['owner', 'reception'].includes(user?.role);
  const canStartEncounter = ['owner', 'doctor'].includes(user?.role);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 font-heading flex items-center gap-2">
              <Users className="w-7 h-7 text-primary" />
              Patient Queue
            </h1>
            <p className="text-slate-500 mt-1">Manage patient flow and doctor assignments</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={fetchData} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            {canModifyQueue && (
              <Button onClick={() => setShowAddModal(true)} className="gap-2 bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4" />
                Add Patient
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div className="card p-4 text-center">
            <div className="text-3xl font-bold text-amber-600">{stats.waiting || 0}</div>
            <div className="text-sm text-slate-500">Waiting</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-3xl font-bold text-blue-600">{stats.with_nurse || 0}</div>
            <div className="text-sm text-slate-500">With Nurse</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-3xl font-bold text-purple-600">{stats.with_doctor || 0}</div>
            <div className="text-sm text-slate-500">With Doctor</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-3xl font-bold text-emerald-600">{stats.completed || 0}</div>
            <div className="text-sm text-slate-500">Completed</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-3xl font-bold text-gray-600">{stats.cancelled || 0}</div>
            <div className="text-sm text-slate-500">Cancelled</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-3xl font-bold text-slate-800">{stats.total_today || 0}</div>
            <div className="text-sm text-slate-500">Total Today</div>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Search by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="rounded"
            />
            Show Completed
          </label>
        </div>

        {/* Queue Sections */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Walk-ins Section */}
          <div className="card">
            <div className="p-4 border-b border-slate-200 bg-amber-50">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                Walk-ins ({walkIns.length})
              </h2>
            </div>
            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {walkIns.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  No walk-in patients in queue
                </div>
              ) : (
                walkIns.map(item => (
                  <QueueCard
                    key={item.id}
                    item={item}
                    doctors={doctors}
                    onUpdateStatus={handleUpdateStatus}
                    onAssignDoctor={handleAssignDoctor}
                    onStartEncounter={handleStartEncounter}
                    onRemove={handleRemoveFromQueue}
                    formatTime={formatTime}
                    getWaitTime={getWaitTime}
                    canModifyQueue={canModifyQueue}
                    canStartEncounter={canStartEncounter}
                  />
                ))
              )}
            </div>
          </div>

          {/* Appointments Section */}
          <div className="card">
            <div className="p-4 border-b border-slate-200 bg-blue-50">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-blue-600" />
                Appointments ({appointments.length})
              </h2>
            </div>
            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {appointments.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  No appointments in queue
                </div>
              ) : (
                appointments.map(item => (
                  <QueueCard
                    key={item.id}
                    item={item}
                    doctors={doctors}
                    onUpdateStatus={handleUpdateStatus}
                    onAssignDoctor={handleAssignDoctor}
                    onStartEncounter={handleStartEncounter}
                    onRemove={handleRemoveFromQueue}
                    formatTime={formatTime}
                    getWaitTime={getWaitTime}
                    canModifyQueue={canModifyQueue}
                    canStartEncounter={canStartEncounter}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {/* Add to Queue Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Add Patient to Queue</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddToQueue} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Patient *</label>
                <Select value={selectedPatient} onValueChange={setSelectedPatient}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a patient..." />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.full_name} {p.middle_name ? `(${p.middle_name})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Visit Type</label>
                <Select value={queueType} onValueChange={setQueueType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="walk_in">Walk-in</SelectItem>
                    <SelectItem value="appointment">Appointment</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Assign Doctor (Optional)</label>
                <Select value={selectedDoctor || 'none'} onValueChange={(val) => setSelectedDoctor(val === 'none' ? '' : val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select doctor..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {doctors.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Normal</SelectItem>
                    <SelectItem value="1">Urgent</SelectItem>
                    <SelectItem value="2">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Reason for visit..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={adding} className="flex-1">
                  {adding ? 'Adding...' : 'Add to Queue'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

// Queue Card Component
const QueueCard = ({ 
  item, doctors, onUpdateStatus, onAssignDoctor, onStartEncounter, onRemove,
  formatTime, getWaitTime, canModifyQueue, canStartEncounter 
}) => {
  const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.waiting;
  const priorityConfig = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG[0];
  const StatusIcon = statusConfig.icon;

  return (
    <div className={`p-4 ${priorityConfig.color} hover:bg-slate-50 transition-colors`}>
      <div className="flex items-start justify-between gap-4">
        {/* Patient Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-slate-800 truncate">
              {item.last_name && item.first_name 
                ? `${item.last_name}, ${item.first_name}${item.middle_name ? ` ${item.middle_name.charAt(0)}.` : ''}`
                : item.patient_name
              }
            </h3>
            {item.priority > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded ${priorityConfig.badge}`}>
                {priorityConfig.label}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3 text-sm text-slate-500">
            {item.phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {item.phone}
              </span>
            )}
            <span>Check-in: {formatTime(item.check_in_time)}</span>
            <span className="font-medium text-amber-600">Wait: {getWaitTime(item.check_in_time)}</span>
          </div>

          {item.assigned_doctor_name && (
            <div className="text-sm text-purple-600 mt-1">
              <Stethoscope className="w-3 h-3 inline mr-1" />
              {item.assigned_doctor_name}
            </div>
          )}

          {item.notes && (
            <p className="text-sm text-slate-600 mt-1 italic">{item.notes}</p>
          )}
        </div>

        {/* Status Badge */}
        <div className={`px-3 py-1 rounded-full text-xs font-medium border ${statusConfig.color} flex items-center gap-1`}>
          <StatusIcon className="w-3 h-3" />
          {statusConfig.label}
        </div>
      </div>

      {/* Actions */}
      {item.status !== 'completed' && item.status !== 'cancelled' && item.status !== 'no_show' && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200">
          {/* Doctor Assignment */}
          {canModifyQueue && (
            <Select 
              value={item.assigned_doctor_id || 'unassigned'} 
              onValueChange={(val) => onAssignDoctor(item.id, val === 'unassigned' ? null : val)}
            >
              <SelectTrigger className="w-40 h-8 text-xs">
                <SelectValue placeholder="Assign Doctor" />
              </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {doctors.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>
                    ))}
                  </SelectContent>
            </Select>
          )}

          {/* Status Transitions */}
          {item.status === 'waiting' && (
            <Button size="sm" variant="outline" onClick={() => onUpdateStatus(item.id, 'with_nurse')} className="h-8 text-xs">
              Send to Nurse
            </Button>
          )}
          {item.status === 'with_nurse' && (
            <Button size="sm" variant="outline" onClick={() => onUpdateStatus(item.id, 'with_doctor')} className="h-8 text-xs">
              Send to Doctor
            </Button>
          )}
          {item.status === 'with_doctor' && canStartEncounter && !item.encounter_id && (
            <Button size="sm" onClick={() => onStartEncounter(item.id)} className="h-8 text-xs gap-1">
              <Play className="w-3 h-3" />
              Start Encounter
            </Button>
          )}
          {item.status === 'with_doctor' && item.encounter_id && (
            <Button size="sm" variant="outline" asChild className="h-8 text-xs">
              <a href={`/encounters/${item.encounter_id}`}>View Encounter</a>
            </Button>
          )}

          {/* Complete / Cancel */}
          {canModifyQueue && (
            <>
              <Button size="sm" variant="ghost" onClick={() => onUpdateStatus(item.id, 'completed')} className="h-8 text-xs text-emerald-600">
                Complete
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onRemove(item.id)} className="h-8 text-xs text-red-600">
                Remove
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PatientQueue;
